/*
 * On This Night: real events, matched to the calendar day you are playing on.
 *
 * The premise only works because of an astronomical accident. Precession moves
 * the stars against the calendar by about 1.4 degrees a century, and the fastest
 * naked-eye star's proper motion covers a seventh of a Moon's width in the same
 * time. So the star field over Pudding Lane on 2 September 1666 is, to the eye,
 * the star field over London on 2 September tonight. The Moon and planets are
 * another matter entirely — they are wherever their periods put them, which is
 * effectively somewhere new every year, and the engine computes them properly.
 *
 * That means an event round is the same puzzle as any other round. Nothing about
 * the navigation changes. What it buys is the reveal.
 *
 * Two consequences worth knowing:
 *
 *   - The year is withheld until the guess is in. The month and day are not a
 *     secret — they are today's, which is the whole conceit — and they are what
 *     the navigation actually needs. The year is what would name the event, and
 *     it is worth almost nothing astronomically, so hiding it costs the player
 *     nothing and keeps the payoff intact.
 *   - Precession is not modelled, so an event's sky is drawn from J2000
 *     positions and sits about 1.4 degrees per century away from the true sky of
 *     its year. For 1985 that is a fifth of a degree; for 1666 it is five, worth
 *     roughly 350 km of longitude at London's latitude. A perfect navigator
 *     loses a little score on the oldest entries. Accepted deliberately: the
 *     alternative is a precession rotation the rest of the game has no use for.
 *
 * `seenBy` is written capitalised: the card shows it as a line of its own under
 * a "The sky as seen by" label, not as the tail of that sentence.
 *
 * It must also *name the thing*. This is the headline of the reveal and often the
 * only line a player reads, so the event itself goes in it — not just the room it
 * happened in. "An audience in the Catskills listening to four and a half minutes
 * of silence" was atmospheric and told nobody it was the premiere of 4′33″; the
 * detail belongs in `note`, where there is room for it, and the name belongs up
 * top. Several entries had the same fault and were rewritten with it.
 *
 * `wiki` is the English Wikipedia article title for that named thing, and the
 * card links to it — the reveal is the one moment the player is being told about
 * something rather than asked to work it out, and the obvious next question is
 * "what was that?". Store the title, not a URL: `wikiUrl` does the escaping, so
 * titles with an en dash or a prime character stay readable here.
 *
 * `at` is the UTC instant. `month`/`day` are the *local* date the event is
 * remembered by, which is not always the same one — the Beatles played their
 * last show on the evening of 29 August in California, which was already the
 * 30th in UTC.
 *
 * Times are as close as the record allows, and several are approximate to the
 * half hour. Where an event ran over hours (a fire, a raid, a night's observing)
 * the instant is chosen inside it at a point that is properly dark; a test holds
 * every entry to the same astronomical night the rest of the game requires.
 */

export const NIGHT_EVENTS = [
  {
    id: 'carrington-aurora-1859',
    month: 8,
    day: 28,
    year: 1859,
    at: '1859-08-29T03:15:00Z',
    lat: 42.36,
    lon: -71.06,
    place: 'Boston, Massachusetts',
    seenBy: 'A continent standing outside under the aurora of the Carrington Event',
    wiki: 'Carrington Event',
    note:
      'The opening act of the great solar storm of 1859. Aurorae burned over the northern states for nights on end, telegraph lines threw sparks, and some operators found they could work with the batteries disconnected.'
  },
  {
    id: 'candlestick-1966',
    month: 8,
    day: 29,
    year: 1966,
    at: '1966-08-30T05:00:00Z',
    lat: 37.713,
    lon: -122.386,
    place: 'Candlestick Park, San Francisco',
    seenBy: 'The last crowd The Beatles ever played to',
    wiki: "The Beatles' 1966 US tour",
    note:
      'Their final commercial concert. Thirty-three minutes, eleven songs, and no announcement that it was the end.'
  },
  {
    id: 'sts8-1983',
    month: 8,
    day: 30,
    year: 1983,
    at: '1983-08-30T06:32:00Z',
    lat: 28.608,
    lon: -80.604,
    place: 'Kennedy Space Center, Florida',
    seenBy: 'Everyone watching the first night launch of a Space Shuttle',
    wiki: 'STS-8',
    note:
      'STS-8 lifted off in the dark at 2:32 in the morning, carrying Guion Bluford, the first African American to reach orbit.'
  },
  {
    id: 'alma-1997',
    month: 8,
    day: 31,
    year: 1997,
    at: '1997-08-30T22:23:00Z',
    lat: 48.864,
    lon: 2.301,
    place: 'Pont de l’Alma, Paris',
    seenBy: 'Paris on the night Diana died',
    wiki: 'Death of Diana, Princess of Wales',
    note: 'The crash in the underpass came twenty-three minutes after midnight.'
  },
  {
    id: 'titanic-found-1985',
    month: 9,
    day: 1,
    year: 1985,
    at: '1985-09-01T04:00:00Z',
    lat: 41.726,
    lon: -49.948,
    place: 'the North Atlantic, 41°N 49°W',
    seenBy: 'The watch aboard Knorr, the night they found Titanic',
    wiki: 'Wreck of the Titanic',
    note:
      'A boiler came up on the video feed in the small hours, seventy-three years after the ship went down beneath this same patch of sky.'
  },
  {
    id: 'great-fire-1666',
    month: 9,
    day: 2,
    year: 1666,
    at: '1666-09-02T01:00:00Z',
    lat: 51.51,
    lon: -0.086,
    place: 'Pudding Lane, London',
    seenBy: 'London, an hour into the Great Fire',
    wiki: 'Great Fire of London',
    note:
      'It started in Thomas Farriner’s bakery in the small hours of a Sunday and burned for four days.'
  },
  {
    id: 'baytown-1943',
    month: 9,
    day: 3,
    year: 1943,
    at: '1943-09-03T02:00:00Z',
    lat: 38.13,
    lon: 15.65,
    place: 'the Strait of Messina',
    seenBy: 'The invasion fleet waiting off Calabria for Operation Baytown',
    wiki: 'Operation Baytown',
    note:
      'The hours before Operation Baytown put Allied troops onto the Italian mainland.'
  },
  {
    id: 'allende-1970',
    month: 9,
    day: 4,
    year: 1970,
    at: '1970-09-05T02:00:00Z',
    lat: -33.45,
    lon: -70.667,
    place: 'Santiago, Chile',
    seenBy: 'The crowds in Santiago the night Allende won',
    wiki: '1970 Chilean presidential election',
    note: 'A late-winter night in the southern hemisphere, with the count still coming in.'
  },
  {
    id: 'kolkata-1997',
    month: 9,
    day: 5,
    year: 1997,
    at: '1997-09-05T16:00:00Z',
    lat: 22.572,
    lon: 88.364,
    place: 'Kolkata',
    seenBy: 'Kolkata on the evening Mother Teresa died',
    wiki: 'Mother Teresa',
    note: 'She died at the Missionaries of Charity house a little after half past nine.'
  },
  {
    id: 'ripken-1995',
    month: 9,
    day: 6,
    year: 1995,
    at: '1995-09-07T01:20:00Z',
    lat: 39.284,
    lon: -76.622,
    place: 'Camden Yards, Baltimore',
    seenBy: 'Camden Yards, the night Cal Ripken passed Lou Gehrig',
    wiki: 'Cal Ripken Jr.',
    note:
      'The game went official in the fifth innings, and the ovation ran for twenty-two minutes.'
  },
  {
    id: 'blitz-1940',
    month: 9,
    day: 7,
    year: 1940,
    at: '1940-09-07T22:00:00Z',
    lat: 51.507,
    lon: -0.06,
    place: 'the London docks',
    seenBy: 'The East End on the first night of the Blitz',
    wiki: 'The Blitz',
    note: 'The bombers came back after dark and did not stop for fifty-seven nights.'
  },
  {
    id: 'star-trek-1966',
    month: 9,
    day: 8,
    year: 1966,
    at: '1966-09-09T01:30:00Z',
    lat: 40.713,
    lon: -74.006,
    place: 'New York',
    seenBy: 'Anyone stepping outside after the first Star Trek broadcast',
    wiki: 'The Man Trap',
    note: '“The Man Trap” went out on NBC at half past eight, and the sky was still there afterwards.'
  },
  {
    id: 'amalthea-1892',
    month: 9,
    day: 9,
    year: 1892,
    at: '1892-09-10T06:00:00Z',
    lat: 37.343,
    lon: -121.643,
    place: 'Lick Observatory, Mount Hamilton, California',
    seenBy: 'E. E. Barnard, finding a fifth moon of Jupiter',
    wiki: 'Amalthea (moon)',
    note:
      'The last moon in the solar system discovered by eye alone, and the first found at Jupiter since Galileo.'
  },
  {
    id: 'bikila-1960',
    month: 9,
    day: 10,
    year: 1960,
    at: '1960-09-10T19:30:00Z',
    lat: 41.89,
    lon: 12.492,
    place: 'the Appian Way, Rome',
    seenBy: 'Rome, after Abebe Bikila ran the marathon barefoot',
    wiki: 'Abebe Bikila',
    note:
      'He finished under the Arch of Constantine in the dark, the last miles lit by torches held along the road.'
  },
  {
    id: 'crown-jewels-1792',
    month: 9,
    day: 11,
    year: 1792,
    at: '1792-09-11T23:00:00Z',
    lat: 48.867,
    lon: 2.322,
    place: 'the Garde-Meuble, Place de la Concorde, Paris',
    seenBy: 'The thieves working through the French crown jewels',
    wiki: 'French Crown Jewels',
    note:
      'They came back night after night for a week. The Hope Diamond went out of one of those windows and was not seen again for decades.'
  },
  /*
   * Second entries, one per date. Easy takes the first event of a date and Hard
   * the second, because the tiers now deal different games and a shared
   * historical round would let a game of Easy hand you the answer to that day's
   * Hard one. Every one of these is held to astronomical night by the same test
   * as the rest.
   */
  {
    id: 'enceladus-1789',
    month: 8,
    day: 28,
    year: 1789,
    at: '1789-08-28T22:30:00Z',
    lat: 51.51,
    lon: -0.59,
    place: 'Slough, England',
    seenBy: 'William Herschel, the night he found Enceladus, a moon of Saturn',
    wiki: 'Enceladus',
    note:
      'The first proper night out with the forty-foot telescope, then the largest in the world. Two new moons came out of it inside a fortnight.'
  },
  {
    id: 'four-thirty-three-1952',
    month: 8,
    day: 29,
    year: 1952,
    at: '1952-08-30T02:00:00Z',
    lat: 42.04,
    lon: -74.12,
    place: 'Maverick Concert Hall, Woodstock, New York',
    seenBy: 'The audience at Woodstock, New York, for the premiere of John Cage’s 4′33″',
    wiki: '4′33″',
    note:
      'David Tudor sat down, closed the keyboard lid, and did not play for four minutes and thirty-three seconds. The hall heard wind, rain on the roof, and itself.'
  },
  {
    id: 'late-show-1993',
    month: 8,
    day: 30,
    year: 1993,
    at: '1993-08-31T03:35:00Z',
    lat: 40.757,
    lon: -73.98,
    place: 'the Ed Sullivan Theater, New York',
    seenBy: 'New York, up late for the first Late Show',
    wiki: 'Late Show with David Letterman',
    note: 'Letterman changed networks, moved thirteen blocks across town, and took the 11:35 slot.'
  },
  {
    id: 'trinidad-1962',
    month: 8,
    day: 31,
    year: 1962,
    at: '1962-08-31T04:00:00Z',
    lat: 10.65,
    lon: -61.51,
    place: 'Port of Spain, Trinidad',
    seenBy: 'Port of Spain, at the minute Trinidad and Tobago became independent',
    wiki: 'Trinidad and Tobago',
    note: 'One flag came down at midnight and another went up, both in the dark.'
  },
  {
    id: 'tripoli-1969',
    month: 9,
    day: 1,
    year: 1969,
    at: '1969-09-01T00:30:00Z',
    lat: 32.887,
    lon: 13.191,
    place: 'Tripoli, Libya',
    seenBy: 'Tripoli, in the hours of the coup that brought Gaddafi to power',
    wiki: '1969 Libyan revolution',
    note: 'Young officers took the radio station and the barracks before dawn, while the king was abroad taking a cure.'
  },
  {
    id: 'labor-day-1935',
    month: 9,
    day: 2,
    year: 1935,
    at: '1935-09-03T02:00:00Z',
    lat: 24.82,
    lon: -80.81,
    place: 'Islamorada, the Florida Keys',
    seenBy: 'The Keys, as the Labor Day hurricane came ashore',
    wiki: '1935 Labor Day hurricane',
    note:
      'It made landfall with the lowest pressure ever recorded in a hurricane reaching the United States, and took the railway with it.'
  },
  {
    id: 'athenia-1939',
    month: 9,
    day: 3,
    year: 1939,
    at: '1939-09-03T23:00:00Z',
    lat: 56.7,
    lon: -14.0,
    place: 'the North Atlantic, west of the Hebrides',
    seenBy: 'The lifeboats of the Athenia, hours into the war at sea',
    wiki: 'SS Athenia (1922)',
    note:
      'Torpedoed on the first evening of the war. The survivors spent the night under this sky waiting to be picked up.'
  },
  {
    id: 'abbey-road-1962',
    month: 9,
    day: 4,
    year: 1962,
    at: '1962-09-04T21:00:00Z',
    lat: 51.532,
    lon: -0.178,
    place: 'Abbey Road Studios, London',
    seenBy: 'Abbey Road, the first night The Beatles recorded for EMI',
    wiki: 'Love Me Do',
    note: 'Seven in the evening until ten, and they came out of it with Love Me Do.'
  },
  {
    id: 'fuerstenfeldbruck-1972',
    month: 9,
    day: 5,
    year: 1972,
    at: '1972-09-05T20:40:00Z',
    lat: 48.205,
    lon: 11.267,
    place: 'Fürstenfeldbruck airbase, Bavaria',
    seenBy: 'The airfield outside Munich, at the end of the massacre at the 1972 Olympics',
    wiki: 'Munich massacre',
    note: 'The hostage crisis that had begun before dawn ended here after dark.'
  },
  {
    id: 'lahore-1965',
    month: 9,
    day: 6,
    year: 1965,
    at: '1965-09-05T23:00:00Z',
    lat: 31.55,
    lon: 74.34,
    place: 'the Punjab, outside Lahore',
    seenBy: 'The Punjab, in the last dark hour before the war of 1965 widened',
    wiki: 'India–Pakistan war of 1965',
    note: 'India crossed the international border before dawn, widening the war of 1965.'
  },
  {
    id: 'vegas-1996',
    month: 9,
    day: 7,
    year: 1996,
    at: '1996-09-08T06:15:00Z',
    lat: 36.114,
    lon: -115.173,
    place: 'Las Vegas',
    seenBy: 'The Strip, the night Tupac was shot',
    wiki: 'Murder of Tupac Shakur',
    note: 'Hit at a red light on Flamingo Road, minutes after leaving the Tyson fight.'
  },
  {
    id: 'galveston-1900',
    month: 9,
    day: 8,
    year: 1900,
    at: '1900-09-09T03:00:00Z',
    lat: 29.3,
    lon: -94.8,
    place: 'Galveston, Texas',
    seenBy: 'Galveston, as the sea came over the island',
    wiki: '1900 Galveston hurricane',
    note: 'The deadliest natural disaster in United States history. The island had no seawall then; it built one after.'
  },
  {
    id: 'beijing-1976',
    month: 9,
    day: 9,
    year: 1976,
    at: '1976-09-08T16:10:00Z',
    lat: 39.9,
    lon: 116.4,
    place: 'Zhongnanhai, Beijing',
    seenBy: 'Beijing, ten minutes after Mao died',
    wiki: 'Death and state funeral of Mao Zedong',
    note: 'It happened just after midnight. The announcement waited until the afternoon, and the city slept through it.'
  },
  {
    id: 'baumettes-1977',
    month: 9,
    day: 10,
    year: 1977,
    at: '1977-09-10T02:40:00Z',
    lat: 43.3,
    lon: 5.37,
    place: 'Baumettes prison, Marseille',
    seenBy: 'Marseille, before dawn on the last night of the guillotine',
    wiki: 'Hamida Djandoubi',
    note: 'The last execution by guillotine anywhere in the world. France abolished the death penalty four years later.'
  },
  {
    id: 'kahlenberg-1683',
    month: 9,
    day: 11,
    year: 1683,
    at: '1683-09-11T20:00:00Z',
    lat: 48.21,
    lon: 16.37,
    place: 'the Kahlenberg, above Vienna',
    seenBy: 'The relief army above Vienna, the night before the Battle of Vienna',
    wiki: 'Battle of Vienna',
    note: 'Sobieski brought his army over the wooded ridge in the dark. The siege broke the following afternoon.'
  },
  {
    id: 'biko-1977',
    month: 9,
    day: 12,
    year: 1977,
    at: '1977-09-11T23:00:00Z',
    lat: -25.746,
    lon: 28.188,
    place: 'Pretoria, South Africa',
    seenBy: 'The night Steve Biko died in a police cell',
    wiki: 'Steve Biko',
    note:
      'He had been driven 1,100 km naked in the back of a Land Rover. The inquest found no one responsible.'
  },
  {
    id: 'turkish-coup-1980',
    month: 9,
    day: 12,
    year: 1980,
    at: '1980-09-12T01:00:00Z',
    lat: 39.933,
    lon: 32.859,
    place: 'Ankara, Turkey',
    seenBy: 'Ankara waking to tanks and a curfew',
    wiki: "1980 Turkish coup d'état",
    note:
      'The army moved at four in the morning and announced it on the radio at dawn.'
  },
  {
    id: 'fort-mchenry-1814',
    month: 9,
    day: 13,
    year: 1814,
    at: '1814-09-14T03:00:00Z',
    lat: 39.263,
    lon: -76.579,
    place: 'Fort McHenry, Baltimore',
    seenBy: 'Francis Scott Key watching the bombardment from a truce ship',
    wiki: 'Battle of Baltimore',
    note:
      'Twenty-five hours of rockets and mortars. The flag was still there at dawn, and he wrote it down.'
  },
  {
    id: 'lin-biao-1971',
    month: 9,
    day: 13,
    year: 1971,
    at: '1971-09-12T18:30:00Z',
    lat: 47.32,
    lon: 110.66,
    place: 'Öndörkhaan, Mongolia',
    seenBy: 'The Mongolian steppe where Lin Biao’s aircraft came down',
    wiki: 'Lin Biao',
    note:
      'Mao’s designated successor fled east in a Trident and ran out of fuel over the steppe.'
  },
  {
    id: 'gw150914',
    month: 9,
    day: 14,
    year: 2015,
    at: '2015-09-14T09:50:45Z',
    lat: 30.563,
    lon: -90.774,
    place: 'LIGO Livingston, Louisiana',
    seenBy: 'The instrument that felt two black holes collide',
    wiki: 'First observation of gravitational waves',
    note:
      'A ripple in spacetime from 1.3 billion years ago moved the mirrors by a thousandth of a proton’s width.'
  },
  {
    id: 'moscow-fire-1812',
    month: 9,
    day: 14,
    year: 1812,
    at: '1812-09-14T21:00:00Z',
    lat: 55.751,
    lon: 37.618,
    place: 'Moscow',
    seenBy: 'Napoleon’s army watching Moscow burn around them',
    wiki: 'Fire of Moscow (1812)',
    note:
      'The city burned for four days. Most of the fire engines had been taken away before the French arrived.'
  },
  {
    id: 'sydney-2000',
    month: 9,
    day: 15,
    year: 2000,
    at: '2000-09-15T11:30:00Z',
    lat: -33.847,
    lon: 151.063,
    place: 'Stadium Australia, Sydney',
    seenBy: 'A hundred thousand people watching Cathy Freeman light the water',
    wiki: '2000 Summer Olympics opening ceremony',
    note:
      'The cauldron rose out of a ring of falling water, stuck for four minutes, and then climbed anyway.'
  },
  {
    id: 'lehman-2008',
    month: 9,
    day: 15,
    year: 2008,
    at: '2008-09-15T05:45:00Z',
    lat: 40.755,
    lon: -73.987,
    place: 'Seventh Avenue, New York',
    seenBy: 'Lehman Brothers staff carrying boxes out at two in the morning',
    wiki: 'Bankruptcy of Lehman Brothers',
    note:
      'The largest bankruptcy filing in American history went in at 1:45 a.m., six hundred billion dollars deep.'
  },
  {
    id: 'grito-1810',
    month: 9,
    day: 16,
    year: 1810,
    at: '1810-09-16T11:00:00Z',
    lat: 21.157,
    lon: -100.933,
    place: 'Dolores Hidalgo, Mexico',
    seenBy: 'The parish that woke to Hidalgo ringing the bell for independence',
    wiki: 'Grito de Dolores',
    note:
      'He rang it before dawn and called the town out. Mexico still rings a bell at that hour every year.'
  },
  {
    id: 'tabas-1978',
    month: 9,
    day: 16,
    year: 1978,
    at: '1978-09-16T17:00:00Z',
    lat: 33.596,
    lon: 56.924,
    place: 'Tabas, Iran',
    seenBy: 'What was left of Tabas on the night of the earthquake',
    wiki: '1978 Tabas earthquake',
    note:
      'The town was flattened in under a minute. Fifteen thousand of its twenty thousand people died.'
  },
  {
    id: 'soviet-poland-1939',
    month: 9,
    day: 17,
    year: 1939,
    at: '1939-09-17T01:30:00Z',
    lat: 52.113,
    lon: 26.097,
    place: 'the Polish border near Pinsk',
    seenBy: 'Polish border guards seeing the Red Army cross in the dark',
    wiki: 'Soviet invasion of Poland',
    note:
      'Sixteen days after Germany came in from the west, the Soviet Union came in from the east.'
  },
  {
    id: 'camp-david-1978',
    month: 9,
    day: 17,
    year: 1978,
    at: '1978-09-18T02:30:00Z',
    lat: 38.898,
    lon: -77.036,
    place: 'the White House, Washington',
    seenBy: 'Sadat and Begin signing after thirteen days at Camp David',
    wiki: 'Camp David Accords',
    note:
      'Carter had kept them in the Maryland woods with no press for nearly two weeks.'
  },
  {
    id: 'hammarskjold-1961',
    month: 9,
    day: 18,
    year: 1961,
    at: '1961-09-17T22:13:00Z',
    lat: -12.987,
    lon: 28.65,
    place: 'near Ndola, Northern Rhodesia',
    seenBy: 'The Zambian bush where the UN Secretary-General’s aircraft came down',
    wiki: 'Dag Hammarskjöld',
    note:
      'He was flying to a ceasefire meeting over Katanga. The cause has been reopened repeatedly ever since.'
  },
  {
    id: 'old-price-riots-1809',
    month: 9,
    day: 18,
    year: 1809,
    at: '1809-09-18T21:30:00Z',
    lat: 51.513,
    lon: -0.122,
    place: 'Covent Garden, London',
    seenBy: 'A theatre audience shouting down the new ticket prices',
    wiki: 'Old Price Riots',
    note:
      'The rebuilt Covent Garden opened with raised prices. The audience rioted nightly for sixty-seven nights and won.'
  },
  {
    id: 'peron-1955',
    month: 9,
    day: 19,
    year: 1955,
    at: '1955-09-20T01:00:00Z',
    lat: -34.608,
    lon: -58.372,
    place: 'Buenos Aires, Argentina',
    seenBy: 'Buenos Aires on the night Perón gave up the presidency',
    wiki: 'Revolución Libertadora',
    note:
      'He took refuge on a Paraguayan gunboat in the harbour and left the country from it.'
  },
  {
    id: 'damascus-titan-1980',
    month: 9,
    day: 19,
    year: 1980,
    at: '1980-09-19T08:00:00Z',
    lat: 35.415,
    lon: -92.4,
    place: 'Damascus, Arkansas',
    seenBy: 'A missile silo crew after a dropped socket punctured the fuel tank',
    wiki: '1980 Damascus Titan missile explosion',
    note: 'The warhead was thrown clear into a ditch and did not detonate.'
  },
  {
    id: 'magellan-1519',
    month: 9,
    day: 20,
    year: 1519,
    at: '1519-09-20T04:00:00Z',
    lat: 36.982,
    lon: -6.353,
    place: 'Sanlúcar de Barrameda, Spain',
    seenBy: 'Five ships slipping out to attempt the first circumnavigation',
    wiki: 'Magellan expedition',
    note:
      'Two hundred and seventy men left. Eighteen came back three years later, on one ship.'
  },
  {
    id: 'caban-1979',
    month: 9,
    day: 20,
    year: 1979,
    at: '1979-09-20T22:00:00Z',
    lat: 4.398,
    lon: 18.519,
    place: 'Bangui, Central African Empire',
    seenBy: 'French paratroopers landing at Bangui in the dark',
    wiki: 'Operation Caban',
    note:
      'Bokassa was abroad. By morning the empire he had crowned himself over was a republic again.'
  },
  {
    id: 'malta-1964',
    month: 9,
    day: 21,
    year: 1964,
    at: '1964-09-20T22:00:00Z',
    lat: 35.899,
    lon: 14.514,
    place: 'Floriana, Malta',
    seenBy: 'Malta at the moment the British flag came down',
    wiki: 'Independence Day (Malta)',
    note:
      'The handover was done at midnight on the Granaries, in front of most of the island.'
  },
  {
    id: 'jiji-1999',
    month: 9,
    day: 21,
    year: 1999,
    at: '1999-09-20T17:47:00Z',
    lat: 23.772,
    lon: 120.982,
    place: 'Jiji, Nantou County, Taiwan',
    seenBy: 'Central Taiwan at a quarter to two in the morning',
    wiki: '1999 Jiji earthquake',
    note:
      'The island’s worst quake in a century, and the fault scarp cut a waterfall across a schoolyard.'
  },
  {
    id: 'vela-incident-1979',
    month: 9,
    day: 22,
    year: 1979,
    at: '1979-09-22T00:53:00Z',
    lat: -47,
    lon: 40,
    place: 'the southern Indian Ocean',
    seenBy: 'An American satellite catching a double flash over empty water',
    wiki: 'Vela incident',
    note:
      'Two flashes in the pattern of a nuclear test. Nobody has ever admitted to it.'
  },
  {
    id: 'nintendo-1889',
    month: 9,
    day: 22,
    year: 1889,
    at: '1889-09-22T13:00:00Z',
    lat: 35.003,
    lon: 135.768,
    place: 'Kyoto, Japan',
    seenBy: 'The Kyoto shop that began printing hanafuda cards',
    wiki: 'Nintendo',
    note:
      'Fusajiro Yamauchi made handmade playing cards. The company is older than the aeroplane.'
  },
  {
    id: 'neptune-1846',
    month: 9,
    day: 23,
    year: 1846,
    at: '1846-09-23T22:00:00Z',
    lat: 52.383,
    lon: 13.064,
    place: 'Berlin Observatory',
    seenBy: 'Johann Galle finding Neptune within an hour of being told where to look',
    wiki: 'Discovery of Neptune',
    note:
      'Le Verrier had computed the position from Uranus’s wobble. Galle found it less than a degree away.'
  },
  {
    id: 'martial-law-1972',
    month: 9,
    day: 23,
    year: 1972,
    at: '1972-09-22T18:00:00Z',
    lat: 14.599,
    lon: 120.984,
    place: 'Manila, Philippines',
    seenBy: 'Manila on the night the arrests began under martial law',
    wiki: 'Proclamation No. 1081',
    note:
      'Marcos signed it two days early and dated it back. The radio stations went quiet before anyone was told.'
  },
  {
    id: 'yom-kippur-alert-1973',
    month: 9,
    day: 24,
    year: 1973,
    at: '1973-09-24T20:30:00Z',
    lat: 31.5,
    lon: 34.47,
    place: 'the Sinai front, Egypt',
    seenBy: 'Egyptian engineers rehearsing the crossing of the Suez Canal',
    wiki: 'Yom Kippur War',
    note:
      'They practised at night for months. The water cannon that cut the sand wall was a plumber’s idea.'
  },
  {
    id: 'jim-henson-1955',
    month: 9,
    day: 24,
    year: 1955,
    at: '1955-09-25T02:30:00Z',
    lat: 38.9,
    lon: -77.07,
    place: 'Washington, D.C.',
    seenBy: 'The first television audience for Sam and Friends',
    wiki: 'Sam and Friends',
    note:
      'A five-minute late-night puppet show on WRC-TV. One of the puppets was a lizard called Kermit.'
  },
  {
    id: 'balboa-1513',
    month: 9,
    day: 25,
    year: 1513,
    at: '1513-09-25T09:00:00Z',
    lat: 8.7,
    lon: -78.1,
    place: 'the Darién isthmus, Panama',
    seenBy: 'The first Europeans to see the Pacific from America',
    wiki: 'Vasco Núñez de Balboa',
    note:
      'He climbed the ridge alone so he would be the first to see it, then called the rest up.'
  },
  {
    id: 'shostakovich-1906',
    month: 9,
    day: 25,
    year: 1906,
    at: '1906-09-25T21:00:00Z',
    lat: 59.939,
    lon: 30.316,
    place: 'Saint Petersburg, Russia',
    seenBy: 'The city on the night Shostakovich was born',
    wiki: 'Dmitri Shostakovich',
    note:
      'He would write the Seventh Symphony in this city while it was under siege.'
  },
  {
    id: 'petrov-1983',
    month: 9,
    day: 26,
    year: 1983,
    at: '1983-09-25T21:15:00Z',
    lat: 55.13,
    lon: 37.15,
    place: 'Serpukhov-15 bunker, south of Moscow',
    seenBy: 'Stanislav Petrov deciding the missile warning was a false alarm',
    wiki: 'Stanislav Petrov',
    note:
      'The satellite system reported five American missiles inbound. He reported a malfunction, and was right.'
  },
  {
    id: 'typhoon-vera-1959',
    month: 9,
    day: 26,
    year: 1959,
    at: '1959-09-26T14:00:00Z',
    lat: 34.7,
    lon: 136.9,
    place: 'Ise Bay, Japan',
    seenBy: 'The coast of Ise Bay as the storm surge came ashore',
    wiki: 'Typhoon Vera',
    note:
      'Japan’s deadliest typhoon. It rebuilt the country’s entire approach to disaster planning.'
  },
  {
    id: 'kabul-1996',
    month: 9,
    day: 27,
    year: 1996,
    at: '1996-09-26T22:00:00Z',
    lat: 34.533,
    lon: 69.167,
    place: 'Kabul, Afghanistan',
    seenBy: 'Kabul on the night the Taliban took the city',
    wiki: 'Battle of Kabul (1992–1996)',
    note: 'They entered in the dark and hanged Najibullah before morning.'
  },
  {
    id: 'tonight-show-1954',
    month: 9,
    day: 27,
    year: 1954,
    at: '1954-09-28T03:15:00Z',
    lat: 40.76,
    lon: -73.98,
    place: 'the Hudson Theatre, New York',
    seenBy: 'The first late-night audience for Steve Allen’s Tonight',
    wiki: 'The Tonight Show',
    note:
      'It invented the format: a desk, a band, a monologue, and whoever wandered in off the street.'
  },
  {
    id: 'estonia-1994',
    month: 9,
    day: 28,
    year: 1994,
    at: '1994-09-27T22:50:00Z',
    lat: 59.383,
    lon: 21.683,
    place: 'the Baltic Sea off Utö',
    seenBy: 'The Baltic on the night the MS Estonia went down',
    wiki: 'MS Estonia',
    note:
      'The bow visor failed in a gale. She sank in under an hour, and 852 people died.'
  },
  {
    id: 'syria-1961',
    month: 9,
    day: 28,
    year: 1961,
    at: '1961-09-28T01:00:00Z',
    lat: 33.513,
    lon: 36.292,
    place: 'Damascus, Syria',
    seenBy: 'Damascus in the hours the union with Egypt was broken',
    wiki: "1961 Syrian coup d'état",
    note:
      'Officers moved before dawn and ended the United Arab Republic after three and a half years.'
  },
  {
    id: 'diesel-1913',
    month: 9,
    day: 29,
    year: 1913,
    at: '1913-09-29T22:00:00Z',
    lat: 51.4,
    lon: 2.5,
    place: 'the English Channel, off Antwerp',
    seenBy: 'The night crossing Rudolf Diesel never finished',
    wiki: 'Rudolf Diesel',
    note:
      'He went to his cabin, left his hat and coat folded on the rail, and was gone by morning.'
  },
  {
    id: 'toutatis-2004',
    month: 9,
    day: 29,
    year: 2004,
    at: '2004-09-30T03:00:00Z',
    lat: 18.344,
    lon: -66.753,
    place: 'Arecibo Observatory, Puerto Rico',
    seenBy: 'The dish watching an asteroid pass four lunar distances away',
    wiki: '4179 Toutatis',
    note:
      'Radar showed a tumbling, two-lobed rock with no fixed axis of rotation at all.'
  },
  {
    id: 'gestapu-1965',
    month: 9,
    day: 30,
    year: 1965,
    at: '1965-09-30T17:30:00Z',
    lat: -6.175,
    lon: 106.827,
    place: 'Jakarta, Indonesia',
    seenBy: 'Jakarta on the night the 30 September Movement moved',
    wiki: '30 September Movement',
    note:
      'Six generals were taken before dawn. What followed killed perhaps half a million people.'
  },
  {
    id: 'munich-1938',
    month: 9,
    day: 30,
    year: 1938,
    at: '1938-09-30T00:30:00Z',
    lat: 48.139,
    lon: 11.575,
    place: 'the Führerbau, Munich',
    seenBy: 'Four governments signing away Czechoslovakia at half past one in the morning',
    wiki: 'Munich Agreement',
    note:
      'No Czech was in the room. Chamberlain flew home and called it peace for our time.'
  },
  {
    id: 'nigeria-1960',
    month: 10,
    day: 1,
    year: 1960,
    at: '1960-10-01T00:00:00Z',
    lat: 6.455,
    lon: 3.394,
    place: 'Lagos, Nigeria',
    seenBy: 'Lagos watching the Union Flag come down at midnight',
    wiki: 'Nigeria',
    note:
      'The green and white went up at the stroke of midnight in front of the Race Course crowd.'
  },
  {
    id: 'mitchell-comet-1847',
    month: 10,
    day: 1,
    year: 1847,
    at: '1847-10-02T02:30:00Z',
    lat: 41.283,
    lon: -70.099,
    place: 'Nantucket, Massachusetts',
    seenBy: 'Maria Mitchell sweeping the sky from a rooftop and finding a comet',
    wiki: 'Maria Mitchell',
    note:
      'The King of Denmark had offered a gold medal for a telescopic comet. She got it.'
  },
  {
    id: 'tlatelolco-1968',
    month: 10,
    day: 2,
    year: 1968,
    at: '1968-10-03T03:00:00Z',
    lat: 19.451,
    lon: -99.137,
    place: 'Tlatelolco, Mexico City',
    seenBy: 'The Plaza de las Tres Culturas after the shooting stopped',
    wiki: 'Tlatelolco massacre',
    note:
      'Ten days before the Olympics opened in the same city. The death toll is still disputed.'
  },
  {
    id: 'guinea-1958',
    month: 10,
    day: 2,
    year: 1958,
    at: '1958-10-02T02:00:00Z',
    lat: 9.509,
    lon: -13.712,
    place: 'Conakry, Guinea',
    seenBy: 'Conakry on the first night of independence',
    wiki: 'Guinea',
    note:
      'Guinea was the only colony to vote no to de Gaulle\'s community. France withdrew within months, taking the telephones and the files.'
  },
  {
    id: 'berlin-1990',
    month: 10,
    day: 3,
    year: 1990,
    at: '1990-10-02T22:00:00Z',
    lat: 52.518,
    lon: 13.376,
    place: 'the Reichstag, Berlin',
    seenBy: 'A million people watching Germany reunify at midnight',
    wiki: 'German reunification',
    note:
      'The flag went up at the stroke of midnight and the fireworks ran until dawn.'
  },
  {
    id: 'iraq-1932',
    month: 10,
    day: 3,
    year: 1932,
    at: '1932-10-03T20:30:00Z',
    lat: 33.315,
    lon: 44.366,
    place: 'Baghdad, Iraq',
    seenBy: 'Baghdad on the day the British mandate ended',
    wiki: 'Mandatory Iraq',
    note:
      'Iraq joined the League of Nations as the first mandate to become a sovereign state.'
  },
  {
    id: 'sputnik-1957',
    month: 10,
    day: 4,
    year: 1957,
    at: '1957-10-04T19:28:00Z',
    lat: 45.92,
    lon: 63.342,
    place: 'Baikonur Cosmodrome, Kazakhstan',
    seenBy: 'The pad crew watching Sputnik leave in the dark',
    wiki: 'Sputnik 1',
    note:
      'A polished aluminium sphere with four whip aerials, beeping on a frequency any ham could hear.'
  },
  {
    id: 'orient-express-1883',
    month: 10,
    day: 4,
    year: 1883,
    at: '1883-10-04T20:30:00Z',
    lat: 48.877,
    lon: 2.359,
    place: 'Gare de l’Est, Paris',
    seenBy: 'The first passengers on the Orient Express',
    wiki: 'Orient Express',
    note:
      'Paris to Giurgiu by rail, then a ferry and another train to Constantinople.'
  },
  {
    id: 'chile-plebiscite-1988',
    month: 10,
    day: 5,
    year: 1988,
    at: '1988-10-06T04:00:00Z',
    lat: -33.450,
    lon: -70.670,
    place: 'Santiago, Chile',
    seenBy: 'Santiago waiting through the night for the plebiscite result',
    wiki: '1988 Chilean presidential referendum',
    note:
      'The country was asked whether Pinochet should have eight more years. It said no, and the count was not announced until the small hours.'
  },
  {
    id: 'monty-python-1969',
    month: 10,
    day: 5,
    year: 1969,
    at: '1969-10-05T22:00:00Z',
    lat: 51.518,
    lon: -0.227,
    place: 'BBC Television Centre, London',
    seenBy: 'The first audience for Monty Python’s Flying Circus',
    wiki: "Monty Python's Flying Circus",
    note:
      'Broadcast at ten to eleven on a Sunday night, in a slot the BBC had not decided what to do with.'
  },
  {
    id: 'jazz-singer-1927',
    month: 10,
    day: 6,
    year: 1927,
    at: '1927-10-07T00:30:00Z',
    lat: 40.757,
    lon: -73.985,
    place: 'Warners’ Theatre, New York',
    seenBy: 'The first audience to hear a feature film talk back',
    wiki: 'The Jazz Singer',
    note:
      '“Wait a minute, wait a minute, you ain’t heard nothin’ yet.” Silent film had about two years left.'
  },
  {
    id: 'sadat-1981',
    month: 10,
    day: 6,
    year: 1981,
    at: '1981-10-06T19:30:00Z',
    lat: 30.128,
    lon: 31.401,
    place: 'Nasr City, Cairo',
    seenBy: 'Cairo on the evening Sadat was killed at the parade ground',
    wiki: 'Assassination of Anwar Sadat',
    note:
      'He was shot at the annual parade marking the crossing of the Suez Canal eight years earlier.'
  },
  {
    id: 'luna3-1959',
    month: 10,
    day: 7,
    year: 1959,
    at: '1959-10-07T01:00:00Z',
    lat: 44.5,
    lon: 34,
    place: 'the Crimean tracking station',
    seenBy: 'Soviet engineers waiting for the first pictures of the Moon’s far side',
    wiki: 'Luna 3',
    note:
      'It developed the film on board, scanned it, and radioed the result back. Twenty-nine grainy frames.'
  },
  {
    id: 'kandahar-2001',
    month: 10,
    day: 7,
    year: 2001,
    at: '2001-10-07T16:30:00Z',
    lat: 31.61,
    lon: 65.71,
    place: 'Kandahar, Afghanistan',
    seenBy: 'Kandahar under the first night of air strikes',
    wiki: 'War in Afghanistan (2001–2021)',
    note:
      'Cruise missiles and carrier aircraft, twenty-six days after the towers came down.'
  },
  {
    id: 'chicago-fire-1871',
    month: 10,
    day: 8,
    year: 1871,
    at: '1871-10-09T03:00:00Z',
    lat: 41.858,
    lon: -87.655,
    place: 'DeKoven Street, Chicago',
    seenBy: 'Chicago on the night the fire started in the O’Leary barn',
    wiki: 'Great Chicago Fire',
    note:
      'A dry summer, wooden pavements and a southwest wind. It burned for two days.'
  },
  {
    id: 'kashmir-quake-2005',
    month: 10,
    day: 8,
    year: 2005,
    at: '2005-10-07T22:00:00Z',
    lat: 34.176,
    lon: 73.221,
    place: 'Muzaffarabad, Kashmir',
    seenBy: 'Muzaffarabad in the hours before the earthquake',
    wiki: '2005 Kashmir earthquake',
    note:
      'It came at 8:50 in the morning, when the schools were full. Over 80,000 people died.'
  },
  {
    id: 'guevara-1967',
    month: 10,
    day: 9,
    year: 1967,
    at: '1967-10-09T03:00:00Z',
    lat: -18.999,
    lon: -64.147,
    place: 'La Higuera, Bolivia',
    seenBy: 'The Bolivian village where Che Guevara spent his last night',
    wiki: 'Che Guevara',
    note: 'He was held in the schoolhouse overnight and shot the next day.'
  },
  {
    id: 'imagine-1975',
    month: 10,
    day: 9,
    year: 1975,
    at: '1975-10-09T05:30:00Z',
    lat: 40.764,
    lon: -73.954,
    place: 'Lenox Hill Hospital, New York',
    seenBy: 'New York on the night Sean Lennon was born, on his father’s birthday',
    wiki: 'Sean Lennon',
    note: 'John Lennon stopped recording for five years the same week.'
  },
  {
    id: 'wuchang-1911',
    month: 10,
    day: 10,
    year: 1911,
    at: '1911-10-10T12:30:00Z',
    lat: 30.545,
    lon: 114.342,
    place: 'Wuchang, Hubei',
    seenBy: 'The garrison that mutinied and ended two thousand years of empire',
    wiki: 'Wuchang Uprising',
    note:
      'It began at about eight in the evening with a single shot in the engineers’ barracks.'
  },
  {
    id: 'fiji-1970',
    month: 10,
    day: 10,
    year: 1970,
    at: '1970-10-10T09:00:00Z',
    lat: -18.141,
    lon: 178.442,
    place: 'Suva, Fiji',
    seenBy: 'Suva on the night before independence',
    wiki: 'Fiji',
    note:
      'Ninety-six years of colonial rule ended at ten in the morning, after a night of dancing.'
  },
  {
    id: 'apollo7-1968',
    month: 10,
    day: 11,
    year: 1968,
    at: '1968-10-11T09:00:00Z',
    lat: 28.522,
    lon: -80.683,
    place: 'Cape Kennedy, Florida',
    seenBy: 'The pad before the first crewed Apollo launch',
    wiki: 'Apollo 7',
    note:
      'Twenty-one months after the fire that killed the Apollo 1 crew on the same programme.'
  },
  {
    id: 'boer-war-1899',
    month: 10,
    day: 11,
    year: 1899,
    at: '1899-10-11T19:00:00Z',
    lat: -27.75,
    lon: 29.93,
    place: 'the Natal border, southern Africa',
    seenBy: 'Boer commandos on the border as the ultimatum ran out',
    wiki: 'Second Boer War',
    note:
      'Kruger gave Britain forty-eight hours to withdraw its troops. It did not, and the commandos rode at five.'
  },
  {
    id: 'landfall-1492',
    month: 10,
    day: 12,
    year: 1492,
    at: '1492-10-12T06:00:00Z',
    lat: 24.055,
    lon: -74.48,
    place: 'the Bahamas',
    seenBy: 'A lookout on the Pinta sighting land at two in the morning',
    wiki: 'Voyages of Christopher Columbus',
    note:
      'Rodrigo de Triana called it first. Columbus later claimed the reward for himself.'
  },
  {
    id: 'bali-2002',
    month: 10,
    day: 12,
    year: 2002,
    at: '2002-10-12T15:05:00Z',
    lat: -8.719,
    lon: 115.169,
    place: 'Kuta, Bali',
    seenBy: 'Kuta on the night of the Bali bombings',
    wiki: '2002 Bali bombings',
    note:
      'Two bombs eleven seconds apart on a Saturday night, 202 people killed from 21 countries.'
  },
  {
    id: 'andes-1972',
    month: 10,
    day: 13,
    year: 1972,
    at: '1972-10-14T02:00:00Z',
    lat: -34.761,
    lon: -70.291,
    place: 'the Andes, on the Chile–Argentina border',
    seenBy: 'Survivors of Flight 571 spending their first night in the fuselage',
    wiki: 'Uruguayan Air Force Flight 571',
    note:
      'A rugby team flying to Chile. Sixteen of the forty-five were still alive seventy-two days later.'
  },
  {
    id: 'templars-1307',
    month: 10,
    day: 13,
    year: 1307,
    at: '1307-10-13T03:30:00Z',
    lat: 48.856,
    lon: 2.352,
    place: 'the Paris Temple',
    seenBy: 'Templars taken from their beds before dawn on a Friday the thirteenth',
    wiki: 'Knights Templar',
    note:
      'Philip IV had the sealed orders opened simultaneously across France at first light.'
  },
  {
    id: 'thai-uprising-1973',
    month: 10,
    day: 14,
    year: 1973,
    at: '1973-10-13T21:00:00Z',
    lat: 13.757,
    lon: 100.502,
    place: 'Ratchadamnoen Avenue, Bangkok',
    seenBy: 'Half a million Thais on the avenue through the night',
    wiki: '1973 Thai popular uprising',
    note:
      'The largest demonstration in Thai history to that point. The military government fell within a day.'
  },
  {
    id: 'roosevelt-shot-1912',
    month: 10,
    day: 14,
    year: 1912,
    at: '1912-10-15T02:30:00Z',
    lat: 43.039,
    lon: -87.91,
    place: 'Milwaukee, Wisconsin',
    seenBy: 'A crowd hearing Roosevelt speak with a bullet in his chest',
    wiki: 'Attempted assassination of Theodore Roosevelt',
    note:
      'A folded fifty-page speech and a steel spectacle case slowed the round. He talked for ninety minutes.'
  },
  {
    id: 'gregorian-1582',
    month: 10,
    day: 15,
    year: 1582,
    at: '1582-10-15T21:00:00Z',
    lat: 41.902,
    lon: 12.457,
    place: 'Rome',
    seenBy: 'Catholic Europe on the first night of the Gregorian calendar',
    wiki: 'Gregorian calendar',
    note:
      'The previous day had been the 4th of October. Ten days were simply removed to put Easter back where it belonged.'
  },
  {
    id: 'shenzhou5-2003',
    month: 10,
    day: 15,
    year: 2003,
    at: '2003-10-14T20:00:00Z',
    lat: 40.958,
    lon: 100.291,
    place: 'Jiuquan Satellite Launch Centre, Gobi Desert',
    seenBy: 'The Gobi pad in the hours before China’s first crewed launch',
    wiki: 'Shenzhou 5',
    note:
      'Yang Liwei orbited fourteen times. China became the third country to put a person in space on its own.'
  },
  {
    id: 'black-power-1968',
    month: 10,
    day: 16,
    year: 1968,
    at: '1968-10-17T03:00:00Z',
    lat: 19.303,
    lon: -99.152,
    place: 'Estadio Olímpico Universitario, Mexico City',
    seenBy: 'The stadium that watched two raised fists on the medal podium',
    wiki: '1968 Olympics Black Power salute',
    note:
      'Smith and Carlos went up shoeless in black socks. Peter Norman wore their badge in solidarity and paid for it too.'
  },
  {
    id: 'lop-nur-1964',
    month: 10,
    day: 16,
    year: 1964,
    at: '1964-10-15T21:00:00Z',
    lat: 40.82,
    lon: 89.27,
    place: 'Lop Nur, Xinjiang',
    seenBy: 'The desert tower on the night before China’s first nuclear test',
    wiki: 'Project 596',
    note:
      'A uranium implosion device on a hundred-metre tower. China became the fifth nuclear power the next afternoon.'
  },
  {
    id: 'loma-prieta-1989',
    month: 10,
    day: 17,
    year: 1989,
    at: '1989-10-18T05:00:00Z',
    lat: 37.775,
    lon: -122.419,
    place: 'San Francisco',
    seenBy: 'A blacked-out San Francisco seeing the Milky Way over the city',
    wiki: '1989 Loma Prieta earthquake',
    note:
      'The quake hit during the World Series warm-up. With the power out, people came outside and found the stars.'
  },
  {
    id: 'lealtad-1945',
    month: 10,
    day: 17,
    year: 1945,
    at: '1945-10-18T02:10:00Z',
    lat: -34.608,
    lon: -58.372,
    place: 'the Plaza de Mayo, Buenos Aires',
    seenBy: 'Workers filling the Plaza de Mayo through the night for Perón',
    wiki: 'Loyalty Day (Argentina)',
    note:
      'They came in from the meatpacking suburbs on foot and stayed until he was released and spoke at eleven.'
  },
  {
    id: 'mogadishu-1977',
    month: 10,
    day: 18,
    year: 1977,
    at: '1977-10-17T21:05:00Z',
    lat: 2.014,
    lon: 45.305,
    place: 'Mogadishu airport, Somalia',
    seenBy: 'A hijacked airliner on the runway five minutes after midnight',
    wiki: 'Lufthansa Flight 181',
    note:
      'GSG 9 went in over the wings under cover of a decoy fire on the runway. Seven minutes, all hostages out.'
  },
  {
    id: 'felicette-1963',
    month: 10,
    day: 18,
    year: 1963,
    at: '1963-10-18T03:00:00Z',
    lat: 30.87,
    lon: -3.03,
    place: 'Hammaguir launch site, Algeria',
    seenBy: 'The Sahara range on the morning it launched a cat',
    wiki: 'Félicette',
    note:
      'Félicette reached 157 km and came back by parachute, the only cat ever to go to space and return.'
  },
  {
    id: 'oumuamua-2017',
    month: 10,
    day: 19,
    year: 2017,
    at: '2017-10-19T12:00:00Z',
    lat: 20.708,
    lon: -156.257,
    place: 'Haleakalā Observatory, Maui',
    seenBy: 'The survey telescope that caught the first known interstellar visitor',
    wiki: '1I/ʻOumuamua',
    note:
      'A dark red sliver on a hyperbolic path, already leaving. It came from outside the solar system and is not coming back.'
  },
  {
    id: 'yalu-1950',
    month: 10,
    day: 19,
    year: 1950,
    at: '1950-10-19T13:00:00Z',
    lat: 40.117,
    lon: 124.394,
    place: 'the Yalu River at Andong',
    seenBy: 'Chinese troops crossing the Yalu in darkness to stay unseen',
    wiki: "People's Volunteer Army",
    note:
      'They moved only at night and dug in before dawn. The first American units did not know they were there.'
  },
  {
    id: 'namka-chu-1962',
    month: 10,
    day: 20,
    year: 1962,
    at: '1962-10-19T22:00:00Z',
    lat: 27.75,
    lon: 91.87,
    place: 'the Namka Chu valley, Himalayas',
    seenBy: 'Indian positions on the ridge before the attack at first light',
    wiki: 'Sino-Indian War',
    note:
      'Fought at 4,000 metres by troops with no winter clothing on either approach march.'
  },
  {
    id: 'saturday-night-massacre-1973',
    month: 10,
    day: 20,
    year: 1973,
    at: '1973-10-21T01:30:00Z',
    lat: 38.898,
    lon: -77.036,
    place: 'Washington, D.C.',
    seenBy: 'Washington on the night Nixon fired the special prosecutor',
    wiki: 'Saturday Night Massacre',
    note:
      'The Attorney General resigned rather than do it, and so did his deputy. The third man in line did it.'
  },
  {
    id: 'edison-lamp-1879',
    month: 10,
    day: 21,
    year: 1879,
    at: '1879-10-22T03:00:00Z',
    lat: 40.567,
    lon: -74.334,
    place: 'Menlo Park, New Jersey',
    seenBy: 'Edison’s team watching a carbon filament burn through the night',
    wiki: 'Thomas Edison',
    note:
      'It lasted thirteen and a half hours. They sat up watching it, betting on when it would fail.'
  },
  {
    id: 'valentich-1978',
    month: 10,
    day: 21,
    year: 1978,
    at: '1978-10-21T12:00:00Z',
    lat: -38.7,
    lon: 144.5,
    place: 'the Bass Strait, Australia',
    seenBy: 'The strait where Frederick Valentich stopped transmitting',
    wiki: 'Disappearance of Frederick Valentich',
    note:
      '“It is not an aircraft.” Then seventeen seconds of metallic scraping, and nothing. No wreckage was ever found.'
  },
  {
    id: 'scilly-1707',
    month: 10,
    day: 22,
    year: 1707,
    at: '1707-10-22T21:00:00Z',
    lat: 49.93,
    lon: -6.35,
    place: 'the Isles of Scilly',
    seenBy: 'A British fleet running onto the rocks for want of a longitude',
    wiki: 'Scilly naval disaster of 1707',
    note:
      'Four ships and some 1,500 men lost because nobody could fix their position east to west. Parliament passed the Longitude Act seven years later.'
  },
  {
    id: 'chandrayaan-2008',
    month: 10,
    day: 22,
    year: 2008,
    at: '2008-10-21T20:00:00Z',
    lat: 13.733,
    lon: 80.235,
    place: 'Sriharikota, Andhra Pradesh',
    seenBy: 'The Indian pad in the hours before Chandrayaan-1 left for the Moon',
    wiki: 'Chandrayaan-1',
    note:
      'It found water on the Moon within a year, in the shadowed craters at the southern pole.'
  },
  {
    id: 'el-alamein-1942',
    month: 10,
    day: 23,
    year: 1942,
    at: '1942-10-23T19:40:00Z',
    lat: 30.83,
    lon: 28.95,
    place: 'El Alamein, Egypt',
    seenBy: 'The desert lit by nine hundred guns opening together',
    wiki: 'Second Battle of El Alamein',
    note:
      'The barrage began at twenty to ten at night and was heard in Alexandria, sixty miles away.'
  },
  {
    id: 'hungary-1956',
    month: 10,
    day: 23,
    year: 1956,
    at: '1956-10-23T20:00:00Z',
    lat: 47.498,
    lon: 19.04,
    place: 'Budapest',
    seenBy: 'Budapest pulling down the Stalin statue by torchlight',
    wiki: 'Hungarian Revolution of 1956',
    note:
      'They cut it at the knees and left the boots on the plinth. The boots stayed there for weeks.'
  },
  {
    id: 'zambia-1964',
    month: 10,
    day: 24,
    year: 1964,
    at: '1964-10-24T00:00:00Z',
    lat: -15.417,
    lon: 28.283,
    place: 'Lusaka, Zambia',
    seenBy: 'The Independence Stadium at the stroke of midnight',
    wiki: 'Zambia',
    note:
      'Northern Rhodesia became Zambia as the lights went out and came back on the new flag.'
  },
  {
    id: 'houdini-1926',
    month: 10,
    day: 24,
    year: 1926,
    at: '1926-10-25T02:00:00Z',
    lat: 42.331,
    lon: -83.046,
    place: 'the Garrick Theatre, Detroit',
    seenBy: 'The last audience Houdini ever performed for',
    wiki: 'Harry Houdini',
    note:
      'He had a ruptured appendix and a fever of 104. He finished the show and died a week later.'
  },
  {
    id: 'picasso-1881',
    month: 10,
    day: 25,
    year: 1881,
    at: '1881-10-25T23:30:00Z',
    lat: 36.721,
    lon: -4.42,
    place: 'Málaga, Spain',
    seenBy: 'Málaga at a quarter past eleven, when Picasso was born',
    wiki: 'Pablo Picasso',
    note:
      'The midwife thought he was stillborn. An uncle blew cigar smoke in his face and he objected.'
  },
  {
    id: 'surigao-1944',
    month: 10,
    day: 25,
    year: 1944,
    at: '1944-10-24T18:00:00Z',
    lat: 10,
    lon: 125.35,
    place: 'the Surigao Strait, Philippines',
    seenBy: 'The last night battleships ever fought each other',
    wiki: 'Battle of Leyte Gulf',
    note:
      'Fought in complete darkness by radar. Six American battleships, five of them raised from the mud at Pearl Harbor.'
  },
  {
    id: 'park-1979',
    month: 10,
    day: 26,
    year: 1979,
    at: '1979-10-26T12:00:00Z',
    lat: 37.577,
    lon: 126.977,
    place: 'Gungjeong-dong, Seoul',
    seenBy: 'Seoul on the night the president was shot at dinner',
    wiki: 'Assassination of Park Chung Hee',
    note:
      'His own intelligence chief did it, at a private supper, over how to handle the protests in Busan.'
  },
  {
    id: 'football-association-1863',
    month: 10,
    day: 26,
    year: 1863,
    at: '1863-10-26T19:30:00Z',
    lat: 51.516,
    lon: -0.123,
    place: 'the Freemasons’ Tavern, London',
    seenBy: 'Eleven clubs meeting to agree one set of rules',
    wiki: 'The Football Association',
    note:
      'The argument was over hacking and handling. The clubs that wanted both left and invented rugby.'
  },
  {
    id: 'arkhipov-1962',
    month: 10,
    day: 27,
    year: 1962,
    at: '1962-10-28T01:00:00Z',
    lat: 24,
    lon: -66,
    place: 'the Sargasso Sea',
    seenBy: 'A Soviet submarine crew deciding not to fire a nuclear torpedo',
    wiki: 'Vasily Arkhipov',
    note:
      'B-59 had been depth-charged for hours and was out of contact with Moscow. Two officers agreed to launch. Arkhipov did not.'
  },
  {
    id: 'checkpoint-charlie-1961',
    month: 10,
    day: 27,
    year: 1961,
    at: '1961-10-27T22:00:00Z',
    lat: 52.507,
    lon: 13.39,
    place: 'Friedrichstraße, Berlin',
    seenBy: 'American and Soviet tanks facing each other a hundred metres apart',
    wiki: 'Berlin Crisis of 1961',
    note:
      'Ten against ten, guns loaded, all night. They withdrew one at a time the next morning.'
  },
  {
    id: 'ochi-1940',
    month: 10,
    day: 28,
    year: 1940,
    at: '1940-10-28T01:00:00Z',
    lat: 37.976,
    lon: 23.734,
    place: 'Kifissia, Athens',
    seenBy: 'A Greek prime minister answering an ultimatum at three in the morning',
    wiki: 'Ohi Day',
    note:
      'The Italian ambassador woke him to demand free passage. The answer was no, and Greece has celebrated it since.'
  },
  {
    id: 'march-on-rome-1922',
    month: 10,
    day: 28,
    year: 1922,
    at: '1922-10-28T03:00:00Z',
    lat: 41.903,
    lon: 12.496,
    place: 'the roads into Rome',
    seenBy: 'Blackshirt columns converging on Rome in the rain',
    wiki: 'March on Rome',
    note:
      'The army could have stopped them easily. The King refused to sign the order and made Mussolini prime minister instead.'
  },
  {
    id: 'arpanet-1969',
    month: 10,
    day: 29,
    year: 1969,
    at: '1969-10-30T06:30:00Z',
    lat: 34.07,
    lon: -118.444,
    place: 'Boelter Hall, UCLA',
    seenBy: 'Two graduate students sending the first message between computers',
    wiki: 'ARPANET',
    note:
      'They typed L, then O, then G. The system crashed. The first thing ever sent over the network was “lo”.'
  },
  {
    id: 'mitla-1956',
    month: 10,
    day: 29,
    year: 1956,
    at: '1956-10-29T17:30:00Z',
    lat: 30.02,
    lon: 32.85,
    place: 'the Mitla Pass, Sinai',
    seenBy: 'Israeli paratroopers dropping into the Sinai at dusk',
    wiki: 'Suez Crisis',
    note:
      'The drop was the pretext the British and French had privately agreed to act on.'
  },
  {
    id: 'war-of-worlds-1938',
    month: 10,
    day: 30,
    year: 1938,
    at: '1938-10-31T01:00:00Z',
    lat: 40.757,
    lon: -73.979,
    place: 'CBS Studio One, New York',
    seenBy: 'A radio audience being told Martians had landed in New Jersey',
    wiki: 'The War of the Worlds (1938 radio drama)',
    note:
      'Welles was twenty-three. The panic was smaller than the newspapers claimed, and they had reasons to claim it.'
  },
  {
    id: 'tsar-bomba-1961',
    month: 10,
    day: 30,
    year: 1961,
    at: '1961-10-30T18:00:00Z',
    lat: 73.85,
    lon: 54.5,
    place: 'Novaya Zemlya, in the Arctic',
    seenBy: 'The Arctic range on the night after the largest explosion ever made',
    wiki: 'Tsar Bomba',
    note:
      'Fifty megatons. The flash was seen a thousand kilometres away and the shockwave went round the Earth three times.'
  },
  {
    id: 'stalin-removed-1961',
    month: 10,
    day: 31,
    year: 1961,
    at: '1961-10-31T21:00:00Z',
    lat: 55.754,
    lon: 37.62,
    place: 'Red Square, Moscow',
    seenBy: 'Red Square screened off while Stalin was taken out of the Mausoleum',
    wiki: "Lenin's Mausoleum",
    note:
      'Done overnight, behind plywood, with the square closed for a parade rehearsal. By morning one name was gone from the wall.'
  },
  {
    id: 'egyptair-1999',
    month: 10,
    day: 31,
    year: 1999,
    at: '1999-10-31T06:50:00Z',
    lat: 40.35,
    lon: -69.77,
    place: 'the Atlantic south of Nantucket',
    seenBy: 'The ocean where EgyptAir 990 came down before dawn',
    wiki: 'EgyptAir Flight 990',
    note:
      'Thirty-three minutes out of Kennedy. American and Egyptian investigators never agreed on why.'
  }
];

/*
 * The event for a calendar day, matched on the local month and day the event is
 * remembered by. Returns null on a date with nothing in the corpus, which is the
 * normal case — the game simply plays an ordinary round instead.
 */
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'

];

/*
 * The date an event is remembered by, which is its local one. The round bar
 * shows the UTC date instead, because that is the one paired with the UTC clock
 * the navigation runs on — for a late-evening event in the Americas the two
 * differ by a day, and each is right where it appears.
 */
export function eventDateLabel(event) {
  return `${event.day} ${MONTH_NAMES[event.month - 1]} ${event.year}`;
}

/*
 * Where to read more. Underscores for spaces is Wikipedia's own convention, and
 * encodeURIComponent covers the rest — half a dozen titles here carry an en
 * dash, a prime or an apostrophe, and one is called 4′33″.
 */
export function wikiUrl(event) {
  return `https://en.wikipedia.org/wiki/${encodeURIComponent(event.wiki.replace(/ /g, '_'))}`;
}

// Everything in the corpus for a calendar day, in the order it is declared.
export function eventsForDate(date = new Date()) {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return NIGHT_EVENTS.filter((event) => event.month === month && event.day === day);
}

/*
 * The event for a calendar day and a tier. Easy takes the first entry of the day
 * and Hard the second, so the two tiers never share a historical round — a game
 * of Easy, where the constellations are named and the compass is up, would
 * otherwise hand over the answer to that day's Hard round.
 *
 * A date with only one entry gives it to both, and a date with none returns null
 * and the game deals an ordinary round instead.
 */
export function eventForDate(date = new Date(), tier = 'easy') {
  const found = eventsForDate(date);
  if (found.length === 0) return null;
  return found[tier === 'hard' ? Math.min(1, found.length - 1) : 0];
}
